import { useParams } from 'react-router';
import { getNewLink } from '../../util/workbook-util';
import { translate } from '../../translations/translator';
import AppleIcon from 'assets/icons/AppleIcon';
import AppsIcon from 'assets/icons/AppsIcon';
import WorkbookIcon from 'assets/icons/WorkbookIcon';
import WorksheetMakerIcon from 'assets/images/icons/APlusWorkSheetMaker.png';
import lessonPlannerIcon from 'assets/images/icons/LessonPlanner.png';
import AssignmentTrackerIcon from 'assets/images/icons/AssignmentTracker.png';
import ResourcesIcon from 'assets/images/icons/Resources.png';
import HomeIcon from 'assets/images/icons/Home.png';
import LogoutIcon from 'assets/icons/LogoutIcon';
import HelpIcon from 'assets/icons/QuestionMark';
import WDTIcon from 'assets/icons/WDTIcon';
import WPIcon from 'assets/icons/WPIcon';
import LNFIcon from 'assets/icons/LNFIcon';
import { createEvent } from 'api';
import AppsTranslationVariant from 'translations/variants/apps';
import NavTranslationVariant from 'translations/variants/nav';
import { useSelector } from 'react-redux';
import { AppStoreState } from 'types';

const translateApps = (key: AppsTranslationVariant) => translate('apps', key);
const translateNav = (key: NavTranslationVariant) => translate('nav', key);
const translateNavWithSub = (key, options) => translate('nav', key, options);

export const NavigationItems = (
  props: any,
  isClickAwayOnly: boolean,
  disableAssignmentTracker: boolean
): any => {
  const { bookCode, edition } = useParams();
  const user = useSelector(({ user }: AppStoreState) => user);

  const userId = user?.me?.id;
  return [
    {
      name: translateNav(NavTranslationVariant.home),
      link: getNewLink(props.currentWorkbook?.bookCode, props.currentWorkbook?.edition, 'home'),
      linkType: 'hitt',
      icon: HomeIcon,
      clickAway: false,
      accessibilityLabelDescription: 'home'
    },
    {
      name: translateNav(NavTranslationVariant.studentWorkbook),
      link: getNewLink(
        props.currentWorkbook?.bookCode,
        props.currentWorkbook?.edition,
        'workbook',
        userId
      ),
      linkType: 'hitt',
      icon: WorkbookIcon,
      clickAway: false,
      accessibilityLabelDescription:
        props.currentWorkbook?.type === 'workbook'
          ? translateNavWithSub('activityBookSelectedTag', {
              bookName: props.currentWorkbook?.title
            })
          : translateNav(NavTranslationVariant.noActivityBookSelectedTag),
      accessibilityLabelButton: open =>
        translateNav(
          open
            ? NavTranslationVariant.closeActivityBookMenuTag
            : NavTranslationVariant.openActivityBookMenuTag
        )
    },
    {
      name: translateNav(NavTranslationVariant.teachersGuide),
      link: getNewLink(
        props.currentWorkbook?.bookCode,
        props.currentWorkbook?.edition,
        'teachersGuide',
        userId
      ),
      linkType: 'hitt',
      icon: AppleIcon,
      clickAway: false,
      accessibilityLabelDescription:
        props.currentWorkbook?.type === 'teachersGuide'
          ? translateNavWithSub('teacherGuideSelectedTag', {
              bookName: props.currentWorkbook?.title
            })
          : translateNav(NavTranslationVariant.noTeacherGuideSelectedTag),
      accessibilityLabelButton: open =>
        translateNav(
          open
            ? NavTranslationVariant.closeTeacherGuideMenuTag
            : NavTranslationVariant.openTeacherGuideMenuTag
        )
    },
    {
      name: translateNav(NavTranslationVariant.lessonPlanner),
      link: getNewLink(
        props.currentWorkbook?.bookCode,
        props.currentWorkbook?.edition,
        NavTranslationVariant.lessonPlanner
      ),
      linkType: 'hitt',
      icon: lessonPlannerIcon,
      clickAway: false,
      accessibilityLabelDescription: 'home'
    },
    {
      name: translateNav(NavTranslationVariant.assignmentTracker),
      link: getNewLink(
        props.currentWorkbook?.bookCode,
        props.currentWorkbook?.edition,
        NavTranslationVariant.assignmentTracker
      ),
      linkType: 'hitt',
      icon: AssignmentTrackerIcon,
      clickAway: false,
      accessibilityLabelDescription: 'home',
      disabled: disableAssignmentTracker
    },
    {
      name: translateNav(NavTranslationVariant.resources),
      link: isClickAwayOnly
        ? getNewLink(
            props.currentWorkbook?.bookCode || bookCode,
            props.currentWorkbook?.edition || edition,
            'clickAways'
          )
        : getNewLink(
            props.currentWorkbook?.bookCode || bookCode,
            props.currentWorkbook?.edition || edition,
            'resources'
          ),
      linkType: 'hitt',
      icon: ResourcesIcon,
      clickAway: true,
      accessibilityLabelDescription: translateNav(NavTranslationVariant.resources)
    },
    {
      name: translateNav(NavTranslationVariant.apps),
      link: '#',
      linkType: 'static',
      icon: AppsIcon,
      clickAway: false,
      accessibilityLabelDescription: translateNav(NavTranslationVariant.appsTag),
      accessibilityLabelButton: open =>
        translateNav(
          open ? NavTranslationVariant.closeAppsMenuTag : NavTranslationVariant.openAppsMenuTag
        ),
      children: [
        {
          name: translateApps(AppsTranslationVariant.letterFormation),
          link: getNewLink(props.currentWorkbook?.bookCode, props.currentWorkbook?.edition, 'lnf'),
          linkType: 'hitt',
          icon: LNFIcon,
          clickAway: false
        },
        {
          name: translateApps(AppsTranslationVariant.wdt),
          link: getNewLink(
            props.currentWorkbook?.bookCode,
            props.currentWorkbook?.edition,
            AppsTranslationVariant.wdt
          ),
          linkType: 'hitt',
          icon: WDTIcon,
          clickAway: false
        },
        {
          name: translateApps(AppsTranslationVariant.woodPieces),
          link: getNewLink(props.currentWorkbook?.bookCode, props.currentWorkbook?.edition, 'wp'),
          linkType: 'hitt',
          icon: WPIcon,
          clickAway: false
        }
      ].filter(app => (isClickAwayOnly ? app.clickAway : true))
    },
    {
      name: translateApps(AppsTranslationVariant.worksheetMaker),
      link: getNewLink(props.currentWorkbook?.bookCode, props.currentWorkbook?.edition, 'wm'),
      linkType: 'hitt',
      icon: WorksheetMakerIcon,
      clickAway: false,
      onClick: () => createEvent('externalApp', { section: 'apps', application: 'worksheetMaker' })
    },
    {
      name: translateNav(NavTranslationVariant.support),
      link: getNewLink(
        props.currentWorkbook?.bookCode,
        props.currentWorkbook?.edition,
        NavTranslationVariant.support
      ),
      linkType: 'hitt',
      icon: HelpIcon,
      clickAway: true,
      accessibilityLabelDescription: translateNav(NavTranslationVariant.support)
    },
    {
      name: translateNav(NavTranslationVariant.logout),
      link: '#',
      linkType: 'static',
      icon: LogoutIcon,
      onClick: props.logoutUser,
      clickAway: true,
      accessibilityLabelDescription: translateNav(NavTranslationVariant.logout)
    }
  ]
    .filter(menuItem => (isClickAwayOnly ? menuItem.clickAway : true))
    .filter(menuItem => (menuItem.children ? menuItem.children.length !== 0 : true));
};
